import { useEffect, useState } from 'react';
import OverlayLoader from '../../../../../components/overlayLoader';
import LoanCardList from '../components/LoanCardList';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../../../services/auth/AuthContext';
import fetchApplicationsService from '../../../../../services/api/fetchApplications';

export default function AppliedLoans({ loanType, setCurrentStatusCode }) {
	const { t } = useTranslation();

	const { setIsAuthenticated } = useAuth();
	const navigate = useNavigate();

	const [searchParams] = useSearchParams();

	// No status strip here either: with the approved step gone this section is
	// a single step, so a strip switching between one option is noise. Nothing
	// switches latest_status any more, so the query is scoped by product and
	// submission status alone and lists the whole bucket.
	const source = searchParams.get('source') ?? 'all';
	const campaign = searchParams.get('campaign') ?? 'all';
	const state = searchParams.get('state') ?? 'all';
	const priority = searchParams.get('priority') ?? 'all';
	const dispositions = searchParams.get('dispositions') ?? 'all';
	const sort = searchParams.get('sort') ?? 'default';
	const rejectionReason = searchParams.get('rejectionReason') ?? 'all';
	const searchBy = searchParams.get('search_by') ?? 'phone';
	const searchTerm = searchParams.get('search_term') ?? '';

	const [applications, setApplications] = useState([]);
	const [resellerData, setResellerData] = useState({});
	const [loading, setLoading] = useState(false);

	const [hasMore, setHasMore] = useState(true);
	const [cursor, setCursor] = useState(null);
	const fetchLoanApplicationsData = async (isNewSearch = false, isLoading = true) => {
		try {
			setLoading(isLoading);

			// Check if this is a fresh search or if the flag is explicitly set
			const isInitialLoad = isNewSearch || applications.length === 0;

			// The cursor is the backend's, not ours: under a sort other than
			// newest-id-first, the last row's id alone does not identify the page
			// boundary, so the pair it returns has to come back verbatim.
			const pageCursor = isNewSearch ? null : cursor;

			const response = await fetchApplicationsService.getApplicationsForStatusCode(
				'',
				{
					loan_type_code: loanType,
					source: source,
					campaign: campaign,
					state: state,
					priority: priority,
					dispositions: dispositions,
					sort: sort,
					...(searchTerm && { [searchBy]: searchTerm }),
					...(pageCursor && {
						last_id: pageCursor.last_id,
						last_sort_value: pageCursor.last_sort_value ?? '',
					}),
					limit: 5,
					current_application_status: "submitted",
					rejection_reason: rejectionReason,
				},
				setIsAuthenticated,
				navigate
			);

			if (isInitialLoad) {
				// If it's a new search, replace the applications
				setApplications(response.applications || []);
			} else {
				// If it's pagination, append the new applications
				setApplications(prevApplications => [...prevApplications, ...(response.applications || [])]);
			}

			setResellerData((prev) => ({
				...prev,
				...response.reseller_data,
			}));
			setHasMore(response.has_more);
			setCursor(response.next_cursor ?? null);
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		setApplications([]);
		setCursor(null);
		setHasMore(true);
		fetchLoanApplicationsData(true);
	}, [loanType, source, campaign, state, priority, dispositions, rejectionReason, sort, searchBy, searchTerm]);

	return (
		<Box
			display={'flex'}
			flexDirection={'column'}
			height={'100%'}
			overflow={'auto'}
		>
			<OverlayLoader
				open={loading}
				text={t('loading.submitted') + '...'}
			/>

			{
				applications
					?
					<>
						<LoanCardList
							applications={applications}
							setApplications={setApplications}
							currentStatusCode={null}
							allStatus={null}
							resellerData={resellerData}
							loanType={loanType}
							loadMore={() => fetchLoanApplicationsData(false, false)}
							hasMore={hasMore}
							showStepper={false}
						/>
					</>
					:
					<>
						<Typography>No data available</Typography>
					</>
			}
		</Box>
	);
}
